import type { RunArtifactLink } from '../types';

// One artifact link row — the shared kind-pill + sanitized-anchor treatment used by
// both the Artifacts tab (`ArtifactsPanel`) and the job drawer's compact list
// (`RunArtifactLinks`).
//
// WHY THIS IS ITS OWN MODULE (do not fold it back into ArtifactsPanel):
// `RunArtifactLinks` is reachable from the EAGER route graph
// (routes.tsx → JobDrawer → RunsList → RunArtifactLinks), while `ArtifactsPanel`
// imports `<Markdown>` for its summary.md disclosure. When `LinkRow` lived in
// ArtifactsPanel, importing it across that boundary dragged react-markdown, the
// whole micromark/unified/mdast pipeline, and lowlight's highlight.js grammars
// (~825 kB of source) into the initial bundle — defeating the `RunPage.lazy.ts`
// split that is supposed to keep markdown out of first load. Keeping this leaf
// module dependency-free is what holds that boundary.

const KIND_LABELS: Record<string, string> = {
  pull_request: 'PR',
  issue: 'issue',
  branch: 'branch',
  commit: 'commit',
};

// Defense-in-depth: artifact URLs come from the control plane, but React does not
// sanitize `href`, so a non-http(s) URL (e.g. `javascript:`) would execute on click.
// Only http/https URLs become links; anything else renders as plain (copyable) text.
function isHttpUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

export function LinkRow({ artifact }: { artifact: RunArtifactLink }) {
  const label = KIND_LABELS[artifact.kind] ?? 'link';
  const text = artifact.title ?? artifact.url;
  return (
    <li className="flex items-center gap-2">
      <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">
        {label}
      </span>
      {isHttpUrl(artifact.url) ? (
        <a
          href={artifact.url}
          target="_blank"
          rel="noreferrer"
          aria-label={`${label}: ${text}`}
          className="truncate text-slate-700 underline"
        >
          {text}
        </a>
      ) : (
        <span className="truncate text-slate-700" title={artifact.url}>
          {text}
        </span>
      )}
      {artifact.repo && (
        <span className="ml-auto truncate text-xs text-slate-400">
          {artifact.repo}
          {artifact.ref ? `@${artifact.ref}` : ''}
        </span>
      )}
    </li>
  );
}
